import React, { useState, useRef } from 'react';
import { ArrowLeftCircle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from './ui/tooltip';

export function JoinSessionModal({ popUpText, onSubmit }) {
  const [step, setStep] = useState(1);
  const [mode, setMode] = useState('');
  const [open, setOpen] = useState(false);
  const [showExplanation, setShowExplanation] = useState(null);
  const publicIdRef = useRef('');
  const privateIdRef = useRef('');

  const title = step === 1 ? 'Join existing session' : `Join session as ${mode}`;

  const reset = () => {
    setStep(1);
    setMode('');
    setShowExplanation(null);
  };

  const handleClose = () => {
    setOpen(false);
    reset();
  };

  return (
    <>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="outline" onClick={() => setOpen(true)}>
              Join existing remote session
            </Button>
          </TooltipTrigger>
          {popUpText && <TooltipContent>{popUpText}</TooltipContent>}
        </Tooltip>
      </TooltipProvider>

      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {step === 2 && (
                <button
                  type="button"
                  onClick={() => { setStep(1); setShowExplanation(null); }}
                  className="text-primary-900 hover:opacity-70 transition-opacity"
                >
                  <ArrowLeftCircle className="w-6 h-6" />
                </button>
              )}
              {title}
            </DialogTitle>
          </DialogHeader>
          <DialogDescription asChild>
            <div className="px-6 py-4">
              {step === 1 && (
                <div className="space-y-6">
                  <div className="flex gap-4 justify-center">
                    <Button
                      variant="outline"
                      size="lg"
                      className="rounded-full"
                      onMouseOver={() => setShowExplanation('observer')}
                      onMouseOut={() => setShowExplanation(null)}
                      onClick={() => { setStep(2); setMode('observer'); setShowExplanation(null); }}
                    >
                      As an observer
                    </Button>
                    <Button
                      variant="outline"
                      size="lg"
                      className="rounded-full"
                      onMouseOver={() => setShowExplanation('editor')}
                      onMouseOut={() => setShowExplanation(null)}
                      onClick={() => { setStep(2); setMode('editor'); setShowExplanation(null); }}
                    >
                      As an editor
                    </Button>
                  </div>
                  <div className="text-center min-h-[3rem]">
                    {showExplanation === 'observer' && (
                      <blockquote className="italic text-sm text-gray-500 border-l-2 border-primary-900 pl-3 text-left">
                        In observer mode, you can view the content of the session but not change any content.
                      </blockquote>
                    )}
                    {showExplanation === 'editor' && (
                      <blockquote className="italic text-sm text-gray-500 border-l-2 border-primary-900 pl-3 text-left">
                        In editor mode, you can view and edit the content of the session.
                      </blockquote>
                    )}
                  </div>
                </div>
              )}

              {step === 2 && mode === 'observer' && (
                <div className="space-y-4 text-center">
                  <Input
                    placeholder="Public session id"
                    onChange={(e) => { publicIdRef.current = e.target.value.trim(); }}
                    className="w-full max-w-sm mx-auto"
                  />
                  <Button onClick={() => onSubmit({ sessionIdPublic: publicIdRef.current })}>
                    Join
                  </Button>
                </div>
              )}

              {step === 2 && mode === 'editor' && (
                <div className="space-y-4 text-center">
                  <Input
                    placeholder="Private session id"
                    onChange={(e) => { privateIdRef.current = e.target.value.trim(); }}
                    className="w-full max-w-sm mx-auto"
                  />
                  <Button onClick={() => onSubmit({ sessionIdPrivate: privateIdRef.current })}>
                    Join
                  </Button>
                </div>
              )}
            </div>
          </DialogDescription>
        </DialogContent>
      </Dialog>
    </>
  );
}
